import { Run } from "@quantizr-engineering/common/types/run";
import { Show, showsForRun } from "@quantizr-engineering/common/types/show";

const DATE_FORMAT_OPTIONS = {
  month: "numeric",
  monthName: "short",
  day: "numeric",
  year: "2-digit",
} as const;

export const isValidDate = (dateString: string): boolean => {
  if (!dateString || dateString.length < 8) return false;
  
  // Check for MM/DD/YYYY format (must be complete)
  const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
  // Check for YYYY-MM-DD format (must be complete)
  const yyyymmddRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  
  if (!mmddyyyyRegex.test(dateString) && !yyyymmddRegex.test(dateString)) {
    return false;
  }
  
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

export const prettyDate = (date: Date) => {
  if (!date) return { day: "TBD", month: "TBD" };
  return {
    day: date.toLocaleDateString("en-US", { day: DATE_FORMAT_OPTIONS.day }),
    month: date.toLocaleDateString("en-US", {
      month: DATE_FORMAT_OPTIONS.monthName,
    }),
  };
};

export const shortDateString = (date?: Date): string => {
  if (!date) return "TBD";
  // handler for when rows transform into groups of rows or smth. But just a good sanity check on typeof date === "date" anyways (date isn't a type for that check)
  if (!date.toLocaleDateString) return (date as unknown as string) ?? "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getRunDates = (run: Run, shows: Show[]): string => {
  const runShows = showsForRun(shows, run);
  if (!runShows || runShows.length < 1) return "N/A";

  if (runShows.length === 1) {
    return runShows[0].performanceDate.toLocaleDateString(
      "en-US",
      DATE_FORMAT_OPTIONS
    );
  }

  const dates = runShows.map((show) => show.performanceDate.getTime());

  const startDate = new Date(Math.min(...dates));
  const endDate = new Date(Math.max(...dates));

  return `${startDate.toLocaleDateString(
    "en-US",
    DATE_FORMAT_OPTIONS
  )} - ${endDate.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS)}`;
};

export const getShowDate = (show: Show): string => {
  if (!show.performanceDate) return "TBD";
  return show.performanceDate.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

export const convertStringstoDates = <T>(input: T): T => {
  if (!input) return input;

  if (Array.isArray(input)) {
    return input.map((item) => convertStringstoDates(item)) as T;
  }

  if (typeof input !== "object") return input;

  const dateFields = [
    "createdAt",
    "updatedAt",
    "uploadedAt",
    "performanceDate",
    "lastFetched",
    "effectiveDate",
    "terminationDate",
    "coverageStartDate",
    "coverageEndDate",
  ];

  const transformed = { ...input };

  Object.entries(transformed).forEach(([key, value]) => {
    if (dateFields.includes(key) && typeof value === "string") {
      (transformed as any)[key] = new Date(value);
    } else if (Array.isArray(value)) {
      (transformed as any)[key] = value.map((item) =>
        convertStringstoDates(item)
      );
    } else if (value && typeof value === "object") {
      (transformed as any)[key] = convertStringstoDates(value);
    }
  });

  return transformed;
};
